import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import './StudentManager.css';
import AssignTaskModal from './AssignTaskModal';

const StudentManager = ({ user }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [error, setError] = useState(null);
    const [assignModalStudent, setAssignModalStudent] = useState(null);
    
    // Invite Link Generation State
    const [citiesList, setCitiesList] = useState([]);
    const [inviteCity, setInviteCity] = useState('');
    const [customCity, setCustomCity] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [showInviteSection, setShowInviteSection] = useState(false);
    const [invitePosition, setInvitePosition] = useState('waiter');
    const [invitesList, setInvitesList] = useState([]);
    const [selectedInviteIds, setSelectedInviteIds] = useState([]);

    // Direct Creation State
    const [showCreatePlayerSection, setShowCreatePlayerSection] = useState(false);
    const [departmentsList, setDepartmentsList] = useState([]);
    const [createFormData, setCreateFormData] = useState({
        studentName: '',
        studentLastName: '',
        studentCity: '',
        customCity: '',
        position: 'waiter',
        level: 'trainee',
        username: '',
        password: ''
    });

    const LEVEL_LABELS = {
        trainee: 'Стажер',
        junior: 'Молодший',
        regular: 'Основний',
        senior: 'Старший'
    };

    const navigate = useNavigate();

    const fetchInvites = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/invites`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvitesList(res.data);
        } catch (err) {
            console.error('Error fetching invites:', err);
        }
    };

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const token = localStorage.getItem('token');
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const res = await axios.get(`${API_URL}/students`, config);
                setStudents(res.data);
            } catch (err) {
                console.error('Error fetching students:', err);
                setError(err.response?.data?.error || err.message);
            } finally {
                setLoading(false);
            }
        };
        
        const fetchCities = async () => {
            try {
                const res = await axios.get(`${API_URL}/cities`);
                setCitiesList(res.data);
            } catch (err) {
                console.error('Error fetching cities:', err);
            }
        };

        const fetchDepts = async () => {
            try {
                const res = await axios.get(`${API_URL}/departments`);
                setDepartmentsList(res.data);
                if (res.data.length > 0) {
                    setCreateFormData(prev => ({ ...prev, position: res.data[0].key }));
                    setInvitePosition(res.data[0].key);
                }
            } catch (err) {
                console.error('Error fetching departments:', err);
            }
        };

        fetchStudents();
        fetchCities();
        fetchDepts();
        fetchInvites();
    }, []);

    const handleCreatePlayer = async (e) => {
        e.preventDefault();
        setError(null);
        
        const city = createFormData.studentCity === 'custom' ? createFormData.customCity : createFormData.studentCity;
        if (!city || !city.trim()) {
            setError('Місто обов’язкове');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            const postRes = await axios.post(`${API_URL}/students`, {
                ...createFormData,
                studentCity: city.trim()
            }, config);
            const newStudent = postRes.data.student;

            // Reset form
            setCreateFormData({
                studentName: '',
                studentLastName: '',
                studentCity: '',
                customCity: '',
                position: departmentsList[0]?.key || 'waiter',
                level: 'trainee',
                username: '',
                password: ''
            });
            setShowCreatePlayerSection(false);
            
            // Reload list
            const res = await axios.get(`${API_URL}/students`, config);
            setStudents(res.data);

            if (newStudent) {
                setAssignModalStudent(newStudent);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Не вдалося створити гравця');
        }
    };

    const handleGenerateInvite = async (e) => {
        e.preventDefault();
        const city = inviteCity === 'custom' ? customCity : inviteCity;
        if (!city || !city.trim()) return;

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/invites`, { 
                city: city.trim(),
                position: invitePosition
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const link = `${window.location.origin}/register/${res.data.hash}`;
            setGeneratedLink(link);
            setCopied(false);
            fetchInvites();
        } catch (err) {
            setError(err.response?.data?.error || 'Не вдалося створити посилання');
        }
    };

    const handleDeleteInvite = async (id) => {
        if (!window.confirm('Ви впевнені, що хочете видалити це запрошувальне посилання?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/invites/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchInvites();
        } catch (err) {
            setError(err.response?.data?.error || 'Не вдалося видалити запрошення');
        }
    };

    const handleBulkDeleteInvites = async () => {
        if (selectedInviteIds.length === 0) return;
        if (!window.confirm(`Ви впевнені, що хочете видалити ${selectedInviteIds.length} запрошувальних посилань?`)) return;
        try {
            const token = localStorage.getItem('token');
            await Promise.all(selectedInviteIds.map(id => 
                axios.delete(`${API_URL}/invites/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ));
            setSelectedInviteIds([]);
            fetchInvites();
            alert('Обрані посилання видалено! 🗑️');
        } catch (err) {
            setError(err.response?.data?.error || 'Не вдалося виконати масове видалення');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopySpecific = (linkText) => {
        navigator.clipboard.writeText(linkText);
        alert('Посилання скопійовано! ✅');
    };

    const filteredStudents = students.filter(s => {
        const full = `${s.studentLastName} ${s.studentName}`.toLowerCase();
        const matchesSearch = full.includes(search.toLowerCase());
        const matchesCity = !cityFilter || s.studentCity === cityFilter;
        return matchesSearch && matchesCity;
    });

    const cities = [...new Set(students.map(s => s.studentCity).filter(Boolean))];

    if (loading) return <div className="sm-loading">Завантаження списку гравців...</div>;

    return (
        <div className="student-manager">
            <div className="sm-header">
                <div>
                    <h1>👥 Гравці</h1>
                    <p>Рейтинг та історія проходження тестів</p>
                </div>
                <div className="sm-header-right">
                    {user?.role !== 'trainer' && (
                        <>
                            <button 
                                className="sm-btn-create-toggle"
                                style={{ marginRight: '0.75rem' }}
                                onClick={() => {
                                    setShowCreatePlayerSection(!showCreatePlayerSection);
                                    setShowInviteSection(false);
                                }}
                            >
                                ➕ Створити гравця
                            </button>
                            <button 
                                className="sm-btn-invite-toggle"
                                onClick={() => {
                                    setShowInviteSection(!showInviteSection);
                                    setShowCreatePlayerSection(false);
                                }}
                            >
                                🔗 Запросити гравця
                            </button>
                        </>
                    )}
                    <div className="sm-stats-brief">
                        <div className="sm-stat-item">
                            <span className="sm-stat-val">{students.length}</span>
                            <span className="sm-stat-lab">Всього гравців</span>
                        </div>
                    </div>
                </div>
            </div>

            {showInviteSection && (
                <div className="sm-invite-card">
                    <h3>Створити посилання для реєстрації студента</h3>
                    <form onSubmit={handleGenerateInvite} className="sm-invite-form">
                        <div className="sm-invite-row">
                            <div className="form-group">
                                <label>Оберіть місто реєстрації</label>
                                <select 
                                    value={inviteCity} 
                                    onChange={e => setInviteCity(e.target.value)}
                                    required
                                >
                                    <option value="">Оберіть місто...</option>
                                    {citiesList.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                    <option value="custom">+ Інше місто...</option>
                                </select>
                            </div>
                            
                            {inviteCity === 'custom' && (
                                <div className="form-group">
                                    <label>Введіть назву міста</label>
                                    <input 
                                        type="text" 
                                        value={customCity}
                                        onChange={e => setCustomCity(e.target.value)}
                                        placeholder="Наприклад: Київ"
                                        required
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label>Посада за замовчуванням</label>
                                <select 
                                    value={invitePosition} 
                                    onChange={e => {
                                        setInvitePosition(e.target.value);
                                    }}
                                    required
                                >
                                    {departmentsList.length === 0 ? (
                                        <>
                                            <option value="waiter">🧑‍🍳 Офіціант</option>
                                            <option value="banquet_manager">🥂 Банкетний менеджер</option>
                                            <option value="administrator">💼 Адміністратор</option>
                                        </>
                                    ) : (
                                        departmentsList.map(d => (
                                            <option key={d._id} value={d.key}>{d.name}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <button type="submit" className="sm-btn-generate">
                                Згенерувати
                            </button>
                        </div>
                    </form>

                    {generatedLink && (
                        <div className="sm-generated-container">
                            <input type="text" value={generatedLink} readOnly />
                            <button className="sm-btn-copy" onClick={handleCopy}>
                                {copied ? 'Скопійовано! ✅' : 'Скопіювати'}
                            </button>
                        </div>
                    )}

                    {invitesList.length > 0 && (
                        <div className="sm-invites-list-section" style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-main)' }}>Активні посилання для реєстрації</h4>
                                {selectedInviteIds.length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255, 77, 79, 0.1)', padding: '5px 12px', borderRadius: '6px', border: '1px solid rgba(255, 77, 79, 0.2)' }}>
                                        <span style={{ fontSize: '0.85rem', color: '#ff4d4f', fontWeight: 'bold' }}>Вибрано: {selectedInviteIds.length}</span>
                                        <button 
                                            type="button"
                                            onClick={handleBulkDeleteInvites}
                                            className="btn-ghost"
                                            style={{ padding: '3px 10px', fontSize: '0.75rem', color: '#fff', backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                                        >
                                            🗑️ Видалити обрані
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="sm-invites-table-wrapper" style={{ overflowX: 'auto' }}>
                                <table className="sm-invites-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                                            <th style={{ padding: '8px', width: '40px' }}>
                                                <input 
                                                    type="checkbox"
                                                    checked={invitesList.length > 0 && selectedInviteIds.length === invitesList.length}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setSelectedInviteIds(invitesList.map(i => i._id));
                                                        } else {
                                                            setSelectedInviteIds([]);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </th>
                                            <th style={{ padding: '8px', color: 'var(--text-secondary)' }}>Місто</th>
                                            <th style={{ padding: '8px', color: 'var(--text-secondary)' }}>Посада</th>
                                            <th style={{ padding: '8px', color: 'var(--text-secondary)' }}>Посилання</th>
                                            <th style={{ padding: '8px', color: 'var(--text-secondary)', textAlign: 'right' }}>Дії</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invitesList.map(invite => {
                                            const linkText = `${window.location.origin}/register/${invite.hash}`;
                                            const dept = departmentsList.find(d => d.key === invite.position);
                                            const deptName = dept ? dept.name : invite.position;
                                            const isSelected = selectedInviteIds.includes(invite._id);
                                            return (
                                                <tr key={invite._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: isSelected ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                                    <td style={{ padding: '8px' }}>
                                                        <input 
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setSelectedInviteIds(prev => [...prev, invite._id]);
                                                                } else {
                                                                    setSelectedInviteIds(prev => prev.filter(id => id !== invite._id));
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px', color: 'var(--text-main)' }}>{invite.city}</td>
                                                    <td style={{ padding: '8px', color: 'var(--text-main)' }}>{deptName}</td>
                                                    <td style={{ padding: '8px' }}>
                                                        <span style={{ color: 'var(--accent-blue)', textDecoration: 'underline', wordBreak: 'break-all' }}>{linkText}</span>
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleCopySpecific(linkText)}
                                                            className="btn-ghost"
                                                            style={{ padding: '3px 8px', fontSize: '0.75rem', marginRight: '6px' }}
                                                        >
                                                            📋 Копіювати
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteInvite(invite._id)}
                                                            className="btn-ghost"
                                                            style={{ padding: '3px 8px', fontSize: '0.75rem', color: '#ff4d4f', borderColor: '#ff4d4f' }}
                                                        >
                                                            🗑️ Видалити
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showCreatePlayerSection && (
                <div className="sm-invite-card">
                    <h3>➕ Створити нового гравця</h3>
                    <form onSubmit={handleCreatePlayer} className="sm-create-form">
                        <div className="sm-create-grid">
                            <div className="form-group">
                                <label>Ім'я</label>
                                <input 
                                    type="text" 
                                    value={createFormData.studentName}
                                    onChange={e => setCreateFormData({...createFormData, studentName: e.target.value})}
                                    placeholder="Введіть ім'я"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Прізвище</label>
                                <input 
                                    type="text" 
                                    value={createFormData.studentLastName}
                                    onChange={e => setCreateFormData({...createFormData, studentLastName: e.target.value})}
                                    placeholder="Введіть прізвище"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Логін</label>
                                <input 
                                    type="text" 
                                    value={createFormData.username}
                                    onChange={e => setCreateFormData({...createFormData, username: e.target.value})}
                                    placeholder="Логін для входу"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Пароль</label>
                                <input 
                                    type="password" 
                                    value={createFormData.password}
                                    onChange={e => setCreateFormData({...createFormData, password: e.target.value})}
                                    placeholder="Пароль для входу"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Місто</label>
                                <select 
                                    value={createFormData.studentCity} 
                                    onChange={e => setCreateFormData({...createFormData, studentCity: e.target.value})}
                                    required
                                >
                                    <option value="">Оберіть місто...</option>
                                    {citiesList.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                    <option value="custom">+ Інше місто...</option>
                                </select>
                            </div>
                            {createFormData.studentCity === 'custom' && (
                                <div className="form-group">
                                    <label>Введіть місто</label>
                                    <input 
                                        type="text" 
                                        value={createFormData.customCity}
                                        onChange={e => setCreateFormData({...createFormData, customCity: e.target.value})}
                                        placeholder="Наприклад: Київ"
                                        required
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <label>Посада / Підрозділ</label>
                                <select 
                                    value={createFormData.position} 
                                    onChange={e => setCreateFormData({...createFormData, position: e.target.value})}
                                    required
                                >
                                    {departmentsList.length === 0 ? (
                                        <>
                                            <option value="waiter">🧑‍🍳 Офіціант</option>
                                            <option value="banquet_manager">🥂 Банкетний менеджер</option>
                                            <option value="administrator">💼 Адміністратор</option>
                                        </>
                                    ) : (
                                        departmentsList.map(d => (
                                            <option key={d._id} value={d.key}>{d.name}</option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Рівень</label>
                                <select 
                                    value={createFormData.level} 
                                    onChange={e => setCreateFormData({...createFormData, level: e.target.value})}
                                    required
                                >
                                    <option value="trainee">Стажер</option>
                                    <option value="junior">Молодший</option>
                                    <option value="regular">Основний</option>
                                    <option value="senior">Старший</option>
                                </select>
                            </div>
                        </div>
                        <button type="submit" className="sm-btn-generate" style={{ marginTop: '1.25rem' }}>
                            Створити гравця
                        </button>
                    </form>
                </div>
            )}

            <div className="sm-controls">
                <input 
                    type="text" 
                    placeholder="🔍 Пошук за прізвищем..." 
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="sm-search-input"
                />
                <select 
                    value={cityFilter} 
                    onChange={e => setCityFilter(e.target.value)}
                    className="sm-city-select"
                >
                    <option value="">Усі міста</option>
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div className="sm-table-container">
                <table className="sm-table">
                    <thead>
                        <tr>
                            <th>Гравець</th>
                            <th>Місто</th>
                            <th>Посада</th>
                            <th>Рівень</th>
                            <th>Тестів</th>
                            <th>Середній бал</th>
                            <th>Остання активність</th>
                            <th>Дія</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudents.map(s => (
                            <tr key={s._id} onClick={() => navigate(`/students/${s._id}`)} className={`sm-row-clickable ${s.isBlocked ? 'sm-row-blocked' : ''}`}>
                                <td className="sm-td-name">
                                    <div className="sm-avatar">{s.studentLastName[0]}{s.studentName[0]}</div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{s.studentLastName} {s.studentName}</span>
                                        {s.isBlocked && <span style={{ color: 'red', fontSize: '0.75em' }}>Заблокований</span>}
                                    </div>
                                </td>
                                <td>{s.studentCity || '—'}</td>
                                <td>{departmentsList.find(d => d.key === s.position)?.name || s.position || '—'}</td>
                                <td>
                                    <span className={`sm-level-badge level-${s.level || 'trainee'}`}>
                                        {LEVEL_LABELS[s.level] || 'Стажер'}
                                    </span>
                                </td>
                                <td>{s.totalTests}</td>
                                <td>
                                    <div className="sm-score-badge" style={{ 
                                        backgroundColor: s.avgScore >= 80 ? 'rgba(74, 222, 128, 0.2)' : 
                                                        s.avgScore >= 50 ? 'rgba(251, 191, 36, 0.2)' : 
                                                        'rgba(248, 113, 113, 0.2)',
                                        color: s.avgScore >= 80 ? '#4ade80' : 
                                               s.avgScore >= 50 ? '#fbbf24' : 
                                               '#f87171'
                                    }}>
                                        {s.avgScore}%
                                    </div>
                                </td>
                                <td className="sm-td-date">
                                    {new Date(s.lastActivity).toLocaleDateString('uk-UA')}
                                </td>
                                <td onClick={e => e.stopPropagation()}>
                                    <button 
                                        className="sm-btn-assign"
                                        style={{ 
                                            marginRight: '0.5rem',
                                            background: 'rgba(56, 189, 248, 0.1)',
                                            color: '#38bdf8',
                                            border: '1px solid rgba(56, 189, 248, 0.2)',
                                            padding: '0.4rem 0.8rem',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem',
                                            fontWeight: '600'
                                        }}
                                        onClick={() => setAssignModalStudent(s)}
                                    >
                                        🎯 Призначити
                                    </button>
                                    <button className="sm-btn-view" onClick={() => navigate(`/students/${s._id}`)}>Профіль →</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {error && (
                    <div className="sm-error-msg">⚠️ Помилка: {error}</div>
                )}
                {filteredStudents.length === 0 && !loading && !error && (
                    <div className="sm-empty">Гравців не знайдено</div>
                )}
            </div>
            {assignModalStudent && (
                <AssignTaskModal 
                    isOpen={!!assignModalStudent}
                    onClose={() => setAssignModalStudent(null)}
                    student={assignModalStudent}
                />
            )}
        </div>
    );
};

export default StudentManager;
